import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IPOAvatar, IPOButton, IPOAccount } from '@hylaa-npm/ipod';

import PageContainer, { IPageRef } from '@/components/Page';
import Empty from '@/components/Empty';
import { PSearchTool, SearchEnum } from '@/components/PSearchTool';
import { PUploadFn } from '@/components/PUpload';

import { BASEURL } from '@/apis';
import {
	changeStaffBasic,
	changeStaffRole,
	changeStaffStatus,
	getStaff,
	getStaffs,
} from '@/apis/account';

import { DisabledStaffWrap } from './style';

const StaffDisabled: FC<{}> = () => {
	const { t } = useTranslation();
	const pageRef = useRef<IPageRef>();
	const cardWidth = 244;
	const cardHeight = 136;
	const cardTop = 18;
	const cardLeft = 18;

	const [staffs, setStaffs] = useState<IPOAccount.IStaff[]>([]);
	const [staff, setStaff] = useState<IPOAccount.IStaff>();

	const [total, setTotal] = useState<number>(0);
	const [pagination, setPagination] = useState<IPagination>({ take: 0, offset: 0 });
	const [name, setName] = useState<string>('');

	useEffect(() => {
		if (pageRef.current) {
			const take = pageRef.current?.pageSize;
			setPagination({ take, offset: 0 });
		}
	}, [pageRef]);

	useEffect(() => {
		getStaffList(name, pagination);
	}, [JSON.stringify(name), JSON.stringify(pagination)]);

	const getStaffList: (param: string, pagination: IPagination) => void = (param, pagination) => {
		if (pagination.take > 0) {
			getStaffs(0, param, pagination).then((res) => {
				setStaffs(res.list);
				setTotal(res.pagination.size ? res.pagination.size : res.list.length);
			});
		}
	};

	const handleEnableStaff: () => Promise<void> = () => {
		return new Promise((res) => {
			changeStaffStatus(staff?.id ?? '', 'enable').then(() => {
				setStaffs((state) => {
					return [...state.filter((cStaff) => cStaff.id !== staff?.id ?? '')];
				});
				res();
			});
		});
	};

	const handleUpdateStaffRole: (role: IPOAccount.StaffRoleEnum) => Promise<void> = (role) => {
		return new Promise((res) => {
			changeStaffRole(staff?.id ?? '', role);
			res();
		});
	};

	const handleUpdateStaffBasic: (basic: IPOAccount.IEditAccount) => Promise<void> = (basic) => {
		return new Promise((res) => {
			changeStaffBasic(staff?.id ?? '', basic);
			res();
		});
	};

	const getSearchEle: () => ReactNode = () => {
		return (
			<PSearchTool
				tools={[{ key: 'name', placeholder: t('common.plInput'), type: SearchEnum.INPUT }]}
				handleSearch={(value) => {
					setName(value.name);
					setPagination({ ...pagination, offset: 0 });
				}}
			/>
		);
	};

	const getContentEle: () => ReactNode = () => {
		const getEmptyType: () => 'search' | 'nodata' = () => {
			if (name) {
				return 'search';
			} else {
				return 'nodata';
			}
		};
		return (
			<>
				{staffs.length > 0 ? (
					<DisabledStaffWrap
						cardWidth={cardWidth}
						cardHeight={cardHeight}
						cardTop={cardTop}
						cardLeft={cardLeft}>
						{staffs.map((staff: IPOAccount.IStaff) => (
							<div className="disabled--wrap">
								<div className="top--wrap">
									<div className="left-wrap">
										<div className="name-major">
											{staff.name2 ? staff.name2 : staff.name ? staff.name : staff.mobile}
										</div>
										<div className="name-infior">
											{staff.name && staff.name2 && <span>{staff.name}</span>}
										</div>
										<div className="mobile">{staff.mobile}</div>
									</div>
									<div className="right-wrap">
										<IPOAvatar
											size={60}
											info={{
												avatar: staff.avatar,
												name: staff.name,
												id: staff.id,
												name2: staff.name2,
												mobile: staff.mobile,
												baseUrl: BASEURL,
											}}
										/>
									</div>
								</div>
								<div className="bottom--wrap">
									<IPOButton onClick={() => getStaff(staff.id).then((res) => setStaff(res))}>
										{t('staff.active')}
									</IPOButton>
								</div>
							</div>
						))}
					</DisabledStaffWrap>
				) : (
					<Empty type={getEmptyType()} tips={getEmptyType() === 'nodata' ? '暂无数据' : ''} />
				)}
			</>
		);
	};

	return (
		<>
			<PageContainer
				pageRef={pageRef}
				pageTitle={t('staff.staffDisabled')}
				searchEle={getSearchEle()}
				contentTitle={t('staff.staffList')}
				contentEle={getContentEle()}
				pagi={{
					total,
					handlePageChange: (offset) => setPagination({ ...pagination, ...{ offset } }),
					pageType: 'card',
					cardInfo: {
						width: cardWidth,
						height: cardHeight,
						rowGap: cardLeft,
						colGap: cardTop,
					},
				}}
			/>
			{staff && (
				<IPOAccount.EnableStaff
					uploadEle={
						<PUploadFn.User
							avatar={staff.avatar}
							changeAvatar={(id) => setStaff({ ...staff, avatar: id })}
						/>
					}
					user={staff}
					isShowEnable={!!staff}
					showRight
					handleChange={() => setStaff(undefined)}
					handleEnableChange={() => handleEnableStaff()}
					handleUpdateRole={(role) => handleUpdateStaffRole(role)}
					handleUpdateBasic={(basic) => handleUpdateStaffBasic(basic)}
				/>
			)}
		</>
	);
};

export default StaffDisabled;
